import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { GetToken } from '@/App'
import type { UtmKey, UtmValues } from '@/components/builder/UtmSection'
import { DrawerPanel } from '@/components/layout/DrawerPanel'
import { PreviewPane } from '@/components/layout/PreviewPane'
import { toTagDefinitionsState, useLinkBuilderDataEntry } from '@/hooks/useLinkBuilderDataEntry'
import { buildUrl } from '@/lib/buildUrl'
import { composeParams } from '@/lib/composeParams'
import * as api from '@/lib/linkBuilderApi'
import { normaliseCustomKey } from '@/lib/normaliseKey'
import * as optionsApi from '@/lib/optionsSourcesApi'
import { loadState, saveState } from '@/lib/persistence'
import {
  chainAncestors,
  chainDescendants,
  deriveImpliedKeys,
  derivePickEmissions,
  derivePickerBindings,
  type PickerBinding,
} from '@/lib/pickerBindings'
import { applyPreset } from '@/lib/presetApply'
import {
  queryParametersEqual,
  toPresetQueryParameters,
  type PresetQueryParameter,
} from '@/lib/presetSave'
import type { FetchPickerOptions, PickerOption } from '@/lib/projectOptions'
import type { Subscription } from '@/lib/subscription'
import type { TagEntry } from '@/lib/tagEntry'
import { toastApiError } from '@/lib/toastApiError'

interface BuilderProps {
  active: Subscription
  available: Subscription[]
  onPickSubscription: (sub: Subscription) => void
  getToken: GetToken
}

export function Builder({
  active,
  available,
  onPickSubscription,
  getToken,
}: BuilderProps) {
  const subscriptionCode = active.code

  // `<Builder key={active.code}>` in App.tsx means a fresh mount per
  // subscription; lazy-init is enough, no re-hydrate effect needed.
  const initial = useMemo(() => loadState(subscriptionCode), [subscriptionCode])

  const [baseUrl, setBaseUrl] = useState(initial?.baseUrl ?? '')
  const [loadedUrl, setLoadedUrl] = useState(initial?.baseUrl ?? '')
  const [utm, setUtm] = useState<UtmValues>(initial?.utm ?? {})
  const [tags, setTags] = useState<TagEntry[]>(initial?.tags ?? [])
  const { state: dataEntry, refetch } = useLinkBuilderDataEntry({
    getToken,
    subscriptionCode,
  })
  const tagDefinitions = useMemo(() => toTagDefinitionsState(dataEntry), [dataEntry])
  const presets = useMemo(
    () => (dataEntry.status === 'ready' ? dataEntry.presets : []),
    [dataEntry],
  )
  const defaultBaseUrl = dataEntry.status === 'ready' ? dataEntry.defaultBaseUrl : null

  const [focusTagId, setFocusTagId] = useState<string | null>(null)
  const [activePresetId, setActivePresetId] = useState<string | null>(null)
  const [appliedSnapshot, setAppliedSnapshot] = useState<PresetQueryParameter[] | null>(null)
  const [isBusy, setIsBusy] = useState(false)

  const baseUrlFocusedRef = useRef(false)
  const pendingIframeUrlRef = useRef<string | null>(null)

  useEffect(() => {
    saveState(subscriptionCode, { baseUrl, utm, tags })
  }, [subscriptionCode, baseUrl, utm, tags])

  useEffect(() => {
    if (initial?.baseUrl || !defaultBaseUrl) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot pre-fill from server-provided default when localStorage is empty
    setBaseUrl((prev) => (prev === '' ? defaultBaseUrl : prev))
  }, [defaultBaseUrl, initial])

  const handleUtmAdd = useCallback((key: UtmKey) => {
    setUtm((prev) => (key in prev ? prev : { ...prev, [key]: '' }))
  }, [])

  const handleUtmChange = useCallback((key: UtmKey, value: string) => {
    setUtm((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleUtmRemove = useCallback((key: UtmKey) => {
    setUtm((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const pickerBindings = useMemo<ReadonlyMap<string, PickerBinding>>(() => {
    if (tagDefinitions.status !== 'ready') return new Map()
    return derivePickerBindings(tagDefinitions.definitions)
  }, [tagDefinitions])

  const handleAnalyticsTagAdd = useCallback(
    (queryKey: string) => {
      const wanted = [...chainAncestors(queryKey, pickerBindings), queryKey]
      const added = wanted
        .filter((key) => !tags.some((t) => t.key === key))
        .map((key) => ({ id: crypto.randomUUID(), key, value: '' }))
      if (added.length === 0) return
      setTags([...tags, ...added])
      setFocusTagId(added[0]?.id ?? null)
    },
    [tags, pickerBindings],
  )

  const handleCustomTagAdd = useCallback(() => {
    setTags((prev) => [
      ...prev,
      { id: crypto.randomUUID(), key: '', value: '', rawKey: '' },
    ])
  }, [])

  const handleTagChangeRawKey = useCallback((id: string, rawKey: string) => {
    setTags((prev) =>
      prev.map((t) => (t.id === id ? { ...t, rawKey, key: normaliseCustomKey(rawKey) } : t)),
    )
  }, [])

  // Editing the text breaks any pick: a typed value must never carry an id.
  const handleTagChangeValue = useCallback((id: string, value: string) => {
    setTags((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t
        const next = { ...t, value }
        delete next.pickedValue
        return next
      }),
    )
  }, [])

  const handleTagPick = useCallback((id: string, option: PickerOption) => {
    setTags((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, value: option.label, pickedValue: option.value } : t,
      ),
    )
  }, [])

  const handleTagRemove = useCallback(
    (id: string) => {
      const removed = tags.find((t) => t.id === id)
      if (removed === undefined) return
      const orphaned = new Set(chainDescendants(removed.key, pickerBindings))
      const kept = tags.filter((t) => t.id !== id && !orphaned.has(t.key))
      const restored = tags
        .map((tag, index) => ({ tag, index }))
        .filter((x) => x.tag.id === id || orphaned.has(x.tag.key))
      setTags(kept)
      setFocusTagId(null)
      if (restored.length > 1) {
        const name =
          tagDefinitions.status === 'ready'
            ? (tagDefinitions.definitions.find((d) => d.queryKey === removed.key)?.name ??
              removed.key)
            : removed.key
        const linked = restored.length - 1
        toast(`Removed ${name} and ${linked} linked field${linked === 1 ? '' : 's'}`, {
          action: {
            label: 'Undo',
            // Put back only what went, so anything done meanwhile survives.
            onClick: () =>
              setTags((current) => {
                const next = [...current]
                for (const { tag, index } of restored) {
                  if (next.some((c) => c.key === tag.key)) continue
                  next.splice(Math.min(index, next.length), 0, tag)
                }
                return next
              }),
          },
        })
      }
    },
    [tags, pickerBindings, tagDefinitions],
  )

  const handleResetAll = useCallback(() => {
    setUtm({})
    setTags([])
    setActivePresetId(null)
    setAppliedSnapshot(null)
  }, [])

  const analyticsKeys = useMemo(() => {
    if (tagDefinitions.status !== 'ready') return new Set<string>()
    return new Set(tagDefinitions.definitions.map((d) => d.queryKey))
  }, [tagDefinitions])

  const fetchPickerOptions = useCallback<FetchPickerOptions>(
    async (query, signal) => {
      const token = await getToken()
      if (token === null) return []
      return optionsApi.fetchOptions({ token, subscriptionCode, signal }, query)
    },
    [getToken, subscriptionCode],
  )

  const pickEmissions = useMemo(
    () => derivePickEmissions(tags, pickerBindings),
    [tags, pickerBindings],
  )

  const impliedKeys = useMemo(
    () => deriveImpliedKeys(tags, pickEmissions, pickerBindings),
    [tags, pickEmissions, pickerBindings],
  )

  const currentQueryParameters = useMemo(
    () => toPresetQueryParameters({ utm, tags, pickEmissions }),
    [utm, tags, pickEmissions],
  )

  const isModified = useMemo(() => {
    if (appliedSnapshot === null) return false
    return !queryParametersEqual(appliedSnapshot, currentQueryParameters)
  }, [appliedSnapshot, currentQueryParameters])

  const handleApplyPreset = useCallback(
    (presetId: string) => {
      const preset = presets.find((p) => p.id === presetId)
      if (!preset) return
      const applied = applyPreset({ preset, analyticsKeys, pickerBindings })
      setUtm(applied.utm)
      setTags(applied.tags)
      setActivePresetId(preset.id)
      setAppliedSnapshot(preset.queryParameters)
    },
    [presets, analyticsKeys, pickerBindings],
  )

  const requireToken = useCallback(async (): Promise<string | null> => {
    const token = await getToken()
    if (token === null) {
      toast.error('Sign in to manage presets')
      return null
    }
    return token
  }, [getToken])

  const handleSaveNewPreset = useCallback(
    async (name: string) => {
      const token = await requireToken()
      if (!token) return
      setIsBusy(true)
      try {
        const created = await api.createPreset(
          { token, subscriptionCode },
          { name, queryParameters: currentQueryParameters },
        )
        if (!created?.id || !created.revisionId) {
          toast.error('Failed to save preset', { description: 'Unexpected response from server.' })
          return
        }
        toast.success(`Saved preset “${name}”`)
        setActivePresetId(created.id)
        setAppliedSnapshot(currentQueryParameters)
        refetch()
      } catch (err) {
        toastApiError(err, 'Failed to save preset')
      } finally {
        setIsBusy(false)
      }
    },
    [currentQueryParameters, subscriptionCode, refetch, requireToken],
  )

  const handleUpdateActivePreset = useCallback(async () => {
    const preset = presets.find((p) => p.id === activePresetId)
    if (!preset) return
    const token = await requireToken()
    if (!token) return
    setIsBusy(true)
    try {
      await api.updatePreset(
        { token, subscriptionCode },
        preset.revisionId,
        { name: preset.name, queryParameters: currentQueryParameters },
      )
      toast.success(`Updated preset “${preset.name}”`)
      setAppliedSnapshot(currentQueryParameters)
      refetch()
    } catch (err) {
      toastApiError(err, 'Failed to update preset')
    } finally {
      setIsBusy(false)
    }
  }, [activePresetId, presets, currentQueryParameters, subscriptionCode, refetch, requireToken])

  const handleDeleteActivePreset = useCallback(async () => {
    const preset = presets.find((p) => p.id === activePresetId)
    if (!preset) return
    const token = await requireToken()
    if (!token) return
    setIsBusy(true)
    try {
      await api.deletePreset({ token, subscriptionCode }, preset.revisionId)
      toast.success(`Deleted preset “${preset.name}”`)
      setActivePresetId(null)
      setAppliedSnapshot(null)
      refetch()
    } catch (err) {
      toastApiError(err, 'Failed to delete preset')
    } finally {
      setIsBusy(false)
    }
  }, [activePresetId, presets, subscriptionCode, refetch, requireToken])

  const hasAnyParams = Object.keys(utm).length > 0 || tags.length > 0

  const handleBaseUrlFocus = useCallback(() => {
    baseUrlFocusedRef.current = true
  }, [])

  const handleBaseUrlBlur = useCallback(() => {
    baseUrlFocusedRef.current = false
    const pending = pendingIframeUrlRef.current
    if (pending !== null) {
      pendingIframeUrlRef.current = null
      setBaseUrl(pending)
    }
  }, [])

  const handleIframeNavigated = useCallback((url: string) => {
    if (baseUrlFocusedRef.current) {
      pendingIframeUrlRef.current = url
    } else {
      setBaseUrl(url)
    }
  }, [])

  const { params, reservedKeys } = useMemo(
    () => composeParams({ utm, tags, pickEmissions, impliedKeys }),
    [utm, tags, pickEmissions, impliedKeys],
  )

  const outputUrl = useMemo(() => buildUrl(baseUrl, params).url, [baseUrl, params])

  return (
    <div className="flex h-dvh">
      <DrawerPanel
        active={active}
        available={available}
        onPickSubscription={onPickSubscription}
        baseUrl={baseUrl}
        loadedUrl={loadedUrl}
        utm={utm}
        tags={tags}
        reservedKeys={reservedKeys}
        tagDefinitions={tagDefinitions}
        pickerBindings={pickerBindings}
        fetchOptions={fetchPickerOptions}
        focusTagId={focusTagId}
        noToken={dataEntry.status === 'no-token'}
        presets={presets}
        activePresetId={activePresetId}
        isModified={isModified}
        isBusy={isBusy}
        outputUrl={outputUrl}
        onBaseUrlChange={setBaseUrl}
        onBaseUrlFocus={handleBaseUrlFocus}
        onBaseUrlBlur={handleBaseUrlBlur}
        onLoad={() => setLoadedUrl(baseUrl)}
        onUtmAdd={handleUtmAdd}
        onUtmChange={handleUtmChange}
        onUtmRemove={handleUtmRemove}
        onAnalyticsTagAdd={handleAnalyticsTagAdd}
        onCustomTagAdd={handleCustomTagAdd}
        onTagChangeRawKey={handleTagChangeRawKey}
        onTagChangeValue={handleTagChangeValue}
        onTagPick={handleTagPick}
        onTagRemove={handleTagRemove}
        onResetAll={handleResetAll}
        hasAnyParams={hasAnyParams}
        onApplyPreset={handleApplyPreset}
        onSaveNewPreset={handleSaveNewPreset}
        onUpdateActivePreset={handleUpdateActivePreset}
        onDeleteActivePreset={handleDeleteActivePreset}
      />
      <PreviewPane url={loadedUrl} onIframeNavigated={handleIframeNavigated} />
    </div>
  )
}
